import JSZip from 'jszip';
import {
  buildChecklistSourceSnapshot,
  parseChecklistSourceUpload,
  CHECKLIST_CATALOG_IMPACT_ZIP_LIMITS as LIMITS,
  type ChecklistCatalogDeliveryProduct,
} from '@tcvs/shared';

export interface LocalCatalogProduct {
  file: string;
  sourceTargetId: string;
  bytes: Uint8Array;
  sha256: string;
  expected: ChecklistCatalogDeliveryProduct['expected'];
}
export class CatalogFileError extends Error {}
function fail(message: string): never {
  throw new CatalogFileError(message);
}
const admitted = new Set(['3557', '94', '8569', '12518', '228', '68585']);
const canonicalName = (name: string) => name.normalize('NFC').toLocaleLowerCase('en-US');
function checkName(name: string) {
  if (
    !name ||
    name.length > 160 ||
    name.trim() !== name ||
    !name.endsWith('.json') ||
    /[/\\:]/.test(name) ||
    name.includes('..') ||
    [...name].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127)
  )
    fail('Use plain snapshot .json filenames without paths or control characters.');
  if (/^(manifest|delivery|repoint|repoints)(?:[._-]|$)/i.test(name))
    fail('Manifests and sidecars are not accepted. Select only source snapshots.');
}
async function readBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') return new Uint8Array(await blob.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new CatalogFileError('The selected file could not be read.'));
    reader.readAsArrayBuffer(blob);
  });
}
interface Member {
  name: string;
  crc: number;
  raw: number;
  compressed: number;
  offset: number;
  flags: number;
  method: number;
}
/** Inventory raw central/local headers before JSZip can normalize paths or overwrite duplicate names. */
export function inspectCatalogZip(bytes: Uint8Array): Member[] {
  if (bytes.length > LIMITS.maxArchiveBytes || bytes.length < 22)
    fail('ZIP exceeds the archive limit or is incomplete.');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const u16 = (at: number) => {
    if (at < 0 || at + 2 > bytes.length) fail('Incomplete ZIP metadata.');
    return view.getUint16(at, true);
  };
  const u32 = (at: number) => {
    if (at < 0 || at + 4 > bytes.length) fail('Incomplete ZIP metadata.');
    return view.getUint32(at, true);
  };
  let end = -1;
  for (
    let candidate = bytes.length - 22;
    candidate >= Math.max(0, bytes.length - 65557);
    candidate -= 1
  ) {
    if (u32(candidate) === 0x06054b50 && candidate + 22 + u16(candidate + 20) === bytes.length) {
      end = candidate;
      break;
    }
  }
  if (end < 0) fail('ZIP directory is missing.');
  const count = u16(end + 10),
    directory = u32(end + 16),
    size = u32(end + 12);
  if (
    u16(end + 4) ||
    u16(end + 6) ||
    u16(end + 8) !== count ||
    count < 1 ||
    count > LIMITS.maxMembers ||
    directory + size !== end
  )
    fail('ZIP must be a single-volume archive with at most 10 snapshot members.');
  let at = directory,
    total = 0;
  const members: Member[] = [],
    names = new Set<string>();
  const decoder = new TextDecoder('utf-8', { fatal: true });
  const extra = (start: number, length: number) => {
    const stop = start + length;
    while (start < stop) {
      if (start + 4 > stop || start + 4 + u16(start + 2) > stop || u16(start) === 1)
        fail('Unsupported ZIP metadata or ZIP64 archive.');
      start += 4 + u16(start + 2);
    }
  };
  for (let index = 0; index < count; index += 1) {
    if (u32(at) !== 0x02014b50) fail('Invalid ZIP directory entry.');
    const flags = u16(at + 8),
      method = u16(at + 10),
      length = u16(at + 28),
      extraLength = u16(at + 30),
      comment = u16(at + 32);
    const raw = u32(at + 24),
      compressed = u32(at + 20),
      offset = u32(at + 42),
      attributes = u32(at + 38),
      unixType = (attributes >>> 16) & 0xf000;
    if (
      at + 46 + length + extraLength + comment > end ||
      flags & ~0x080e ||
      ![0, 8].includes(method) ||
      u16(at + 34) ||
      attributes & 0x10 ||
      (unixType !== 0 && unixType !== 0x8000)
    )
      fail('Encrypted, linked, directory or unsupported ZIP members are not accepted.');
    let name: string;
    try {
      name = decoder.decode(bytes.subarray(at + 46, at + 46 + length));
    } catch {
      return fail('ZIP filenames must use UTF-8.');
    }
    checkName(name);
    if (names.has(canonicalName(name)))
      fail('ZIP contains duplicate or case/Unicode-colliding filenames.');
    names.add(canonicalName(name));
    if (raw < 1 || raw > LIMITS.maxMemberBytes || compressed > LIMITS.maxArchiveBytes)
      fail('Each snapshot must contain at most 8 MiB of JSON.');
    total += raw;
    if (total > LIMITS.maxExpandedBytes) fail('ZIP expanded contents exceed the delivery limit.');
    extra(at + 46 + length, extraLength);
    members.push({ name, crc: u32(at + 16), raw, compressed, offset, flags, method });
    at += 46 + length + extraLength + comment;
  }
  if (at !== end) fail('ZIP has undeclared directory contents.');
  let next = 0;
  for (const member of [...members].sort((a, b) => a.offset - b.offset)) {
    at = member.offset;
    if (
      at !== next ||
      u32(at) !== 0x04034b50 ||
      u16(at + 6) !== member.flags ||
      u16(at + 8) !== member.method
    )
      fail('ZIP local headers do not match the directory.');
    const length = u16(at + 26),
      extraLength = u16(at + 28);
    let name: string;
    try {
      name = decoder.decode(bytes.subarray(at + 30, at + 30 + length));
    } catch {
      return fail('Invalid local ZIP filename.');
    }
    if (name !== member.name) fail('ZIP contains conflicting filenames.');
    extra(at + 30 + length, extraLength);
    next = at + 30 + length + extraLength + member.compressed;
    if (next > directory) fail('ZIP compressed data overlaps its directory.');
    if (member.flags & 8) {
      if (u32(next) === 0x08074b50) next += 4;
      if (
        u32(next) !== member.crc ||
        u32(next + 4) !== member.compressed ||
        u32(next + 8) !== member.raw
      )
        fail('ZIP data descriptor does not match its directory.');
      next += 12;
    } else if (
      u32(at + 14) !== member.crc ||
      u32(at + 18) !== member.compressed ||
      u32(at + 22) !== member.raw
    )
      fail('ZIP local sizes or checksum do not match.');
  }
  if (next !== directory) fail('ZIP contains undeclared local data.');
  return members;
}
const crcTable = Array.from({ length: 256 }, (_, number) => {
  let crc = number;
  for (let bit = 0; bit < 8; bit += 1) crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
  return crc >>> 0;
});
/** JSZip exposes internalStream at runtime; its bundled declaration omits this public method. */
type StreamingMember = JSZip.JSZipObject & {
  internalStream(type: 'uint8array'): JSZip.JSZipStreamHelper<Uint8Array>;
};
async function inflateMember(
  entry: JSZip.JSZipObject,
  member: Member,
  remaining: number,
  current: () => boolean,
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = [];
    let count = 0,
      crc = 0xffffffff,
      stopped = false;
    const stream = (entry as StreamingMember).internalStream('uint8array');
    const stop = () => {
      stopped = true;
      stream.pause();
      chunks.length = 0;
      reject(new CatalogFileError('ZIP checksum, expanded byte limit or selection changed.'));
    };
    stream
      .on('data', (chunk) => {
        if (stopped) return;
        count += chunk.length;
        if (
          !current() ||
          count > LIMITS.maxMemberBytes ||
          count > remaining ||
          count > member.raw
        ) {
          stop();
          return;
        }
        for (const byte of chunk) crc = crcTable[(crc ^ byte) & 255]! ^ (crc >>> 8);
        chunks.push(chunk);
      })
      .on('error', () => {
        if (!stopped) stop();
      })
      .on('end', () => {
        if (stopped) return;
        if (count !== member.raw || (crc ^ 0xffffffff) >>> 0 !== member.crc || !current()) {
          stop();
          return;
        }
        const result = new Uint8Array(count);
        let offset = 0;
        for (const chunk of chunks) {
          result.set(chunk, offset);
          offset += chunk.length;
        }
        resolve(result);
      })
      .resume();
  });
}
export async function inspectCatalogDeliveryFiles(
  files: readonly File[],
  current = () => true,
): Promise<LocalCatalogProduct[]> {
  const result: LocalCatalogProduct[] = [],
    targets = new Set<string>(),
    names = new Set<string>();
  let total = 0;
  const accept = async (name: string, bytes: Uint8Array) => {
    checkName(name);
    if (!current()) fail('Selection changed.');
    if (names.has(canonicalName(name)) || result.length >= LIMITS.maxMembers)
      fail('Duplicate filenames or too many snapshots.');
    names.add(canonicalName(name));
    total += bytes.length;
    if (bytes.length < 1 || bytes.length > LIMITS.maxMemberBytes || total > LIMITS.maxExpandedBytes)
      fail('Snapshot bytes exceed the delivery limit.');
    let value: unknown;
    try {
      value = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
    } catch {
      return fail('A snapshot is not valid UTF-8 JSON.');
    }
    const parsed = parseChecklistSourceUpload(value);
    if (!parsed.ok)
      fail(
        'Every member must be a schema version 7 source snapshot; manifests and sidecars are unsupported.',
      );
    const upload = parsed.upload;
    const target = upload.product.sourceTargetId;
    if (!admitted.has(target))
      fail('A source target is outside the six admitted staging fixtures.');
    if (targets.has(target)) fail('Duplicate source targets are not accepted.');
    targets.add(target);
    const built = buildChecklistSourceSnapshot(upload.product, upload.rows, {
      ...upload,
      expectedTargetId: target,
    });
    if (!built.ok)
      fail('A source snapshot failed catalog validation. Correct its fields before staging.');
    const snapshot = built.snapshot;
    const sha256 = Array.from(
      new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)),
      (byte) => byte.toString(16).padStart(2, '0'),
    ).join('');
    result.push({
      file: name,
      sourceTargetId: target,
      bytes,
      sha256,
      expected: {
        rosterCount: snapshot.rosterCount,
        parallelDefinitionCount: snapshot.parallelDefinitionCount,
        subsetDefinitionCount: snapshot.subsetDefinitionCount,
        snapshotFingerprint: snapshot.fingerprint,
      },
    });
  };
  if (!files.length || files.length > LIMITS.maxMembers)
    fail('Select one to ten snapshot files or ZIP archives.');
  for (const file of files) {
    if (!current()) fail('Selection changed.');
    if (file.name.endsWith('.zip')) {
      if (file.size > LIMITS.maxArchiveBytes) fail('Each ZIP archive must be at most 20 MiB.');
      const bytes = await readBytes(file),
        members = inspectCatalogZip(bytes);
      if (members.length + result.length > LIMITS.maxMembers)
        fail('At most ten snapshot members are accepted.');
      const archive = await JSZip.loadAsync(bytes, { createFolders: false, checkCRC32: false });
      if (Object.keys(archive.files).length !== members.length)
        fail('ZIP member names are ambiguous.');
      for (const member of members) {
        const entry = archive.files[member.name];
        if (
          !entry ||
          entry.dir ||
          (entry.unsafeOriginalName !== undefined && entry.unsafeOriginalName !== member.name)
        )
          fail('ZIP member names are ambiguous.');
        await accept(
          member.name,
          await inflateMember(entry, member, LIMITS.maxExpandedBytes - total, current),
        );
      }
    } else {
      checkName(file.name);
      if (file.size > LIMITS.maxMemberBytes) fail('Each JSON snapshot must be at most 8 MiB.');
      await accept(file.name, await readBytes(file));
    }
  }
  return result;
}
